/**
 * The tour's card: a frame the director places once per scene and a step's
 * content inside it. It decides nothing about where it stands. CSS custom
 * properties give each variant its type ramp and spacing.
 */

import { PROMPT_DIALOG_STYLES } from "@/template-prompt/promptDialogStyles";
import {
  forwardRef,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { TOUR_UI_ATTR } from "./chrome";
import { INSET, type CardPlace, type Dock } from "./layout";

/** The card's fade out, before the next scene is set. */
export const EXIT_MS = 180;
export const ENTER_MS = 200;
export const SLIDE_MS = 240;
/** The card's box following its content, in place. */
const RESIZE_MS = 200;
/** How long the content takes to settle once the card is up. */
export const SETTLE_MS = 330;
export const NUDGE_MS = 700;

export interface CardNote {
  kind: "slow" | "nudge";
  text: string;
}

interface TourCardProps {
  dock: Dock;
  /** The record's step, for whoever reads the screen. */
  step: number;
  /** Remounts the content when it changes. */
  contentKey: string;
  width: number;
  /** Null until the director has placed it. */
  place: CardPlace | null;
  shown: boolean;
  /** A short move slides; everything else changes while the card is out. */
  sliding: boolean;
  done: boolean;
  nudged: boolean;
  note: CardNote | null;
  children: ReactNode;
}

const variantClass = (dock: Dock): string =>
  dock === "splash"
    ? "_co_onb_overlay_splash"
    : `_co_onb_overlay_compact${
        dock === "center" ? " _co_onb_overlay_center_compact" : ""
      }${dock === "stageRight" ? " _co_onb_overlay_right_center" : ""}`;

export const TourCard = forwardRef<HTMLDivElement, TourCardProps>(
  function TourCard(
    {
      dock,
      step,
      contentKey,
      width,
      place,
      shown,
      sliding,
      done,
      nudged,
      note,
      children,
    },
    ref,
  ) {
    // Content waits to settle until its card first comes up; after that it
    // goes out and comes back with the card.
    const seen = useRef<string | null>(null);
    if (shown) seen.current = contentKey;
    const fresh = !shown && seen.current !== contentKey;
    // Out of sight it stays where it last stood: a fade is never seen
    // anywhere else.
    const last = useRef<CardPlace | null>(null);
    if (place) last.current = place;
    const at = place ?? last.current;
    // While it is up the card's height is its content's, set outright, so a
    // change of content resizes the box smoothly where it stands.
    const body = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<number | null>(null);
    useLayoutEffect(() => {
      const el = body.current;
      const content = el?.parentElement;
      const card = content?.parentElement;
      if (!el || !content || !card) return;
      const read = () => {
        const style = getComputedStyle(content);
        setHeight(
          el.offsetHeight +
            parseFloat(style.paddingTop) +
            parseFloat(style.paddingBottom) +
            (card.offsetHeight - card.clientHeight),
        );
      };
      read();
      if (typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(read);
      observer.observe(el);
      return () => observer.disconnect();
    }, [contentKey]);
    return createPortal(
      <>
        <style>{PROMPT_DIALOG_STYLES}</style>
        <style>{CARD_STYLES}</style>
        <div
          ref={ref}
          {...{ [TOUR_UI_ATTR]: "" }}
          data-tour-card={contentKey}
          data-tour-step={step}
          data-tour-shown={shown ? "true" : "false"}
          data-tour-fresh={fresh ? "" : undefined}
          data-tour-done={done ? "true" : undefined}
          className={`_co_onb _co_onb_overlay _co_tour_card ${variantClass(dock)}${
            sliding ? " _co_tour_sliding" : ""
          }${nudged ? " _co_tour_nudged" : ""}`}
          style={{
            left: at?.left ?? 0,
            top: at?.top ?? 0,
            width: place?.width ?? width,
            height: shown && height ? height : undefined,
            maxHeight: `calc(100vh - ${INSET * 2}px)`,
          }}
          role="dialog"
          aria-label="Cohesive onboarding"
          aria-hidden={shown ? undefined : true}
        >
          <div key={contentKey} className="_co_onb_overlay_content">
            <div ref={body} className="_co_onb_overlay_body">
              {children}
            </div>
          </div>
          <span aria-hidden className="_co_tour_check">
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
              <path
                d="M2.5 6.4l2.3 2.3 4.7-5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <div
            className="_co_tour_note"
            data-tour-note={note?.kind}
            role="status"
          >
            <span>{note?.text ?? ""}</span>
          </div>
        </div>
      </>,
      document.body,
    );
  },
);

// Per-variant tokens (`--co-onb-fs-*`, `--co-onb-stack-*`, `--co-onb-pad-*`)
// consumed by the step components; tuned against compact ~520 wide and
// splash ~860 wide on a 1440 viewport.
const CARD_STYLES = `
  ._co_tour_card {
    position: fixed;
    z-index: 55;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    color: var(--co-fg);
    font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Inter', 'Geist', sans-serif;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition:
      opacity ${EXIT_MS}ms ease-in,
      visibility 0s linear ${EXIT_MS}ms;
  }
  ._co_tour_card[data-tour-shown="true"] {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transition:
      opacity ${ENTER_MS}ms ease-out,
      height ${RESIZE_MS}ms cubic-bezier(0.2, 0, 0, 1);
  }
  ._co_tour_card._co_tour_sliding[data-tour-shown="true"] {
    transition:
      opacity ${ENTER_MS}ms ease-out,
      height ${RESIZE_MS}ms cubic-bezier(0.2, 0, 0, 1),
      left ${SLIDE_MS}ms cubic-bezier(0.2, 0, 0, 1),
      top ${SLIDE_MS}ms cubic-bezier(0.2, 0, 0, 1);
  }
  /* The content settles once, quickly, when the card comes up. */
  ._co_tour_card ._co_onb_settle_1,
  ._co_tour_card ._co_onb_settle_2,
  ._co_tour_card ._co_onb_settle_3,
  ._co_tour_card ._co_onb_settle_4 { animation-duration: 200ms; }
  ._co_tour_card ._co_onb_settle_1 { animation-delay: 0ms; }
  ._co_tour_card ._co_onb_settle_2 { animation-delay: 20ms; }
  ._co_tour_card ._co_onb_settle_3 { animation-delay: 40ms; }
  ._co_tour_card ._co_onb_settle_4 { animation-delay: 60ms; }
  ._co_tour_card[data-tour-fresh] ._co_onb_settle_1,
  ._co_tour_card[data-tour-fresh] ._co_onb_settle_2,
  ._co_tour_card[data-tour-fresh] ._co_onb_settle_3,
  ._co_tour_card[data-tour-fresh] ._co_onb_settle_4 {
    animation: none;
    opacity: 0;
  }
  ._co_onb_overlay_splash {
    --co-onb-fs-eyebrow: 12px;
    --co-onb-fs-heading: 38px;
    --co-onb-fs-heading-display: 56px;
    --co-onb-fs-lede: 17px;
    --co-onb-fs-hint: 12px;
    --co-onb-fs-meta: 16px;
    --co-onb-fs-cta: 14px;
    --co-onb-stack-xs: 6px;
    --co-onb-stack-sm: 12px;
    --co-onb-stack-md: 20px;
    --co-onb-stack-lg: 32px;
    --co-onb-stack-xl: 44px;
    --co-onb-pad-x: 40px;
    --co-onb-pad-y: 36px;
    --co-onb-content-max: 560px;
    --co-onb-dot: 9px;

    background: rgba(243, 239, 230, 0.97);
    backdrop-filter: blur(18px) saturate(1.08);
    -webkit-backdrop-filter: blur(18px) saturate(1.08);
    border: 1px solid var(--co-border);
    border-radius: 22px;
    box-shadow:
      0 1px 2px rgba(15, 12, 8, 0.06),
      0 28px 80px rgba(15, 12, 8, 0.28);
  }
  .dark ._co_onb_overlay_splash {
    background: rgba(26, 26, 22, 0.97);
    box-shadow:
      0 1px 2px rgba(0, 0, 0, 0.4),
      0 32px 96px rgba(0, 0, 0, 0.6);
  }
  ._co_onb_overlay_compact {
    --co-onb-fs-eyebrow: 11px;
    --co-onb-fs-heading: 28px;
    --co-onb-fs-heading-display: 38px;
    --co-onb-fs-lede: 15px;
    --co-onb-fs-hint: 11px;
    --co-onb-fs-meta: 14px;
    --co-onb-fs-cta: 13px;
    --co-onb-stack-xs: 4px;
    --co-onb-stack-sm: 8px;
    --co-onb-stack-md: 14px;
    --co-onb-stack-lg: 22px;
    --co-onb-stack-xl: 30px;
    --co-onb-pad-x: 28px;
    --co-onb-pad-y: 26px;
    --co-onb-content-max: 100%;
    --co-onb-dot: 7px;

    background: var(--co-paper);
    border: 1px solid var(--co-border);
    border-radius: 18px;
    box-shadow:
      0 1px 2px rgba(15, 12, 8, 0.06),
      0 12px 36px rgba(15, 12, 8, 0.18);
  }
  .dark ._co_onb_overlay_compact {
    background: rgba(26, 26, 22, 0.96);
    box-shadow:
      0 1px 2px rgba(0, 0, 0, 0.4),
      0 16px 48px rgba(0, 0, 0, 0.55);
  }
  ._co_onb_overlay_content {
    flex: 1 1 auto;
    width: 100%;
    box-sizing: border-box;
    padding: var(--co-onb-pad-y) var(--co-onb-pad-x);
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
  }
  ._co_onb_overlay_body {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    text-align: left;
    gap: var(--co-onb-stack-md);
  }
  ._co_onb_overlay_right_center { --co-onb-pad-y: var(--co-onb-pad-x); }
  @media (max-height: 759px) {
    ._co_onb_overlay_splash {
      --co-onb-fs-heading: 30px;
      --co-onb-fs-heading-display: 42px;
      --co-onb-fs-lede: 15px;
      --co-onb-fs-meta: 14px;
      --co-onb-stack-md: 14px;
      --co-onb-stack-lg: 20px;
      --co-onb-stack-xl: 26px;
      --co-onb-pad-x: 28px;
      --co-onb-pad-y: 24px;
    }
    ._co_onb_overlay_compact {
      --co-onb-fs-heading: 22px;
      --co-onb-fs-heading-display: 30px;
      --co-onb-fs-lede: 13px;
      --co-onb-fs-meta: 13px;
      --co-onb-fs-cta: 12px;
      --co-onb-stack-sm: 6px;
      --co-onb-stack-md: 10px;
      --co-onb-stack-lg: 14px;
      --co-onb-stack-xl: 18px;
      --co-onb-pad-x: 20px;
      --co-onb-pad-y: 20px;
    }
    ._co_onb_overlay_right_center { --co-onb-pad-y: var(--co-onb-pad-x); }
  }
  /* The step is done: shown for a beat before the card leaves. */
  ._co_tour_check {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--co-rust);
    color: #FBF9F4;
    opacity: 0;
    transform: scale(0.6);
    transition: opacity 160ms ease-out, transform 220ms cubic-bezier(0.2, 0, 0, 1);
    pointer-events: none;
  }
  ._co_tour_card[data-tour-done="true"] ._co_tour_check {
    opacity: 1;
    transform: scale(1);
  }
  /* One quiet line inside the bottom padding: it never changes the box. */
  ._co_tour_note {
    position: absolute;
    left: var(--co-onb-pad-x);
    right: var(--co-onb-pad-x);
    bottom: 3px;
    height: 14px;
    font-size: 11px;
    line-height: 14px;
    color: var(--co-fg-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    opacity: 0;
    transition: opacity 160ms ease;
    pointer-events: none;
  }
  ._co_tour_note[data-tour-note] { opacity: 1; }
  /* A blocked click points here. */
  @keyframes _co_tour_nudge {
    0%, 100% { box-shadow: 0 0 0 0 rgba(184, 110, 73, 0); }
    30% { box-shadow: 0 0 0 6px rgba(184, 110, 73, 0.45); }
  }
  ._co_tour_nudged [data-tour-primary] {
    animation: _co_tour_nudge ${NUDGE_MS}ms ease-out;
  }
  ._co_onb_link {
    appearance: none;
    align-self: flex-start;
    background: none;
    border: none;
    padding: 4px 2px;
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    color: var(--co-rust);
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  ._co_onb_link:hover { color: var(--co-fg); }
  ._co_onb_link:focus-visible {
    outline: 2px solid var(--co-rust);
    outline-offset: 2px;
    border-radius: 4px;
  }
  ._co_onb_quiet {
    appearance: none;
    background: none;
    border: none;
    padding: 2px 4px;
    font: inherit;
    font-size: 12px;
    color: var(--co-fg-muted);
    cursor: pointer;
    border-radius: 4px;
  }
  ._co_onb_quiet:hover:not(:disabled) {
    color: var(--co-fg);
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  ._co_onb_quiet:focus-visible {
    outline: 2px solid var(--co-rust);
    outline-offset: 2px;
  }
  @media (prefers-reduced-motion: reduce) {
    ._co_tour_card,
    ._co_tour_card[data-tour-shown="true"],
    ._co_tour_card._co_tour_sliding[data-tour-shown="true"],
    ._co_tour_check { transition: none; }
    ._co_tour_nudged [data-tour-primary] { animation: none; }
  }
`;
